import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { type BuilderMessage } from "@/lib/promptBuilderUtils";
import { BUILDER_RECEIPT_STEPS, extractBuilderStepResponses } from "@/lib/promptJourneyReceipt";

interface BuilderProgressSidebarProps {
  currentStep: number;
  messages: BuilderMessage[];
  goal?: string;
  selectedTool?: string | null;
}

export function BuilderProgressSidebar({ currentStep, messages, goal, selectedTool }: BuilderProgressSidebarProps) {
  const stepResponses = useMemo(() => extractBuilderStepResponses(messages), [messages]);

  return (
    <div className="p-4">
      {goal && (
        <div className="mb-4 pb-4 border-b border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Objective
          </h3>
          <p className="text-xs text-muted-foreground/80 leading-relaxed">{goal}</p>
        </div>
      )}
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
        Progress
      </h3>
      <div className="flex flex-col">
        {BUILDER_RECEIPT_STEPS.map((step, index) => {
          const isCompleted = currentStep > step.num;
          const isCurrent = currentStep === step.num;
          const isPending = currentStep < step.num;
          const response = stepResponses[step.num];
          const hasResponse = (isCompleted || isCurrent) && response;
          const isLast = index === BUILDER_RECEIPT_STEPS.length - 1;

          return (
            <div key={step.num} className="flex gap-3">
              {/* Left track: circle + line */}
              <div className="flex flex-col items-center flex-shrink-0">
                {/* Circle */}
                <div
                  className={cn(
                    "w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors",
                    isCompleted && "bg-primary border-primary text-primary-foreground",
                    isCurrent && "bg-background border-primary text-primary",
                    isPending && "bg-background border-muted-foreground/30 text-muted-foreground"
                  )}
                >
                  {step.num}
                </div>
                {/* Line stretches to fill remaining height */}
                {!isLast && (
                  <div
                    className={cn(
                      "w-0.5 flex-1 min-h-4 transition-colors",
                      isCompleted ? "bg-primary" : "bg-border"
                    )}
                  />
                )}
              </div>

              {/* Right content: label + response */}
              <div className={cn("flex-1 min-w-0 pt-0.5", !isLast && "pb-4")}>
                <span
                  className={cn(
                    "text-sm font-medium transition-colors block",
                    isCurrent && "text-foreground font-semibold",
                    isCompleted && "text-muted-foreground",
                    isPending && "text-muted-foreground/60"
                  )}
                >
                  {step.label}
                </span>

                {hasResponse && (
                  <p className="text-xs text-muted-foreground/80 mt-1.5 leading-relaxed">
                    {response}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {selectedTool && (
        <div className="mt-4 pt-4 border-t border-border">
          <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Tool Selection
          </h3>
          <p className="text-xs text-muted-foreground/80 leading-relaxed">{selectedTool}</p>
        </div>
      )}
    </div>
  );
}
